import type { ReactNode } from 'react';
import { useAtomValue } from 'jotai';
import { ActionableNotification, InlineNotification } from '@carbon/react';
import classNames from 'classnames';
import plur from 'plur';
import { OutputSection } from 'components/output-section/output-section';
import { FormattedCount } from 'components/formatted-count/formatted-count';
import { format0, formatNumber, formatWholeDollarCurrency } from 'utils/number';
import {
  activeFilterCountAtom,
  interestModelDeltaAtom,
  mapScopeAtom,
  serverFiltersAtom,
  serverPopulationModeAtom,
  typeCurveOverridesAtom,
  inventoryPackageOverridesAtom,
  typeCurveScenarioIdAtom,
} from './atoms';
import { useEval3Page } from './context';
import {
  ExecutiveSummaryError,
  useCalcLifecycle,
  useExecutiveSummary,
  usePopulationCalculationId,
} from './hooks';
import { fmtUsd } from './interest-modeling-format';
import {
  hasInterestModelDelta,
  type InterestModelDelta,
} from './interest-modeling-rollup';
import { EMPTY } from './utils';
import type { ExecutiveSummaryResponse } from './types';
import styles from './executive-summary.module.scss';
import {
  SLOT_CATEGORY_KEYS,
  SLOT_CATEGORY_LABELS,
  WELL_STATUS_SLOT_CATEGORY_KEYS,
  wellStatusCountKey,
} from 'utils/slot-categories';

/**
 * The always-present count tiles — label + how to read the integer off the summary
 * (mock §5 order). Currency / NRA tiles render explicitly (string values), and the
 * Title-Risk tile is conditional, so they aren't in here.
 */
const COUNT_TILES: {
  label: string;
  value: (summary: ExecutiveSummaryResponse) => number;
  /** A Create New Well status's tile, shown only when it has slots (INS-13303). */
  wellStatus?: boolean;
}[] = [
  { label: 'Assets', value: (summary) => summary.assets },
  // One tile per category, in display order (the shared list).
  ...SLOT_CATEGORY_KEYS.map((category) => ({
    label: SLOT_CATEGORY_LABELS[category],
    wellStatus: WELL_STATUS_SLOT_CATEGORY_KEYS.includes(category),
    value: (summary: ExecutiveSummaryResponse) =>
      summary.slotCounts[
        wellStatusCountKey(
          category
        ) as keyof ExecutiveSummaryResponse['slotCounts']
      ] ?? 0,
  })),
  { label: 'Operators', value: (summary) => summary.operators },
  { label: 'Formations', value: (summary) => summary.formations },
];

/**
 * Formats an acreage figure (Net Royalty / Net Mineral Acres) for the strip +
 * narrative — commas, up to 2 dp, the empty indicator when the value is `null` /
 * not yet provided (no completed calc backs it, or the BE hasn't added the key).
 *
 * @param {number | null | undefined} value - The acreage value (nullish → the empty indicator).
 * @return {string} The formatted acreage.
 */
const formatAcres = (value: number | null | undefined): string =>
  formatNumber({
    value,
    minPrecision: 0,
    maxPrecision: 2,
    includeCommas: true,
    fallback: EMPTY,
  });

/**
 * One stat tile — value on top, label beneath (mock §5). The value is a node so a
 * tile can host a string (currency / decimal) or a `<FormattedCount>` count.
 *
 * @param {object} props - The tile label, value node, and warning flag.
 * @param {string} props.label - The label shown beneath the value.
 * @param {ReactNode} props.value - The formatted value node.
 * @param {boolean} [props.warning] - Warning-styled (the Assets-with-Title-Risk tile).
 * @return {JSX.Element} The stat tile.
 */
function StatTile({
  label,
  value,
  warning,
}: {
  label: string;
  value: ReactNode;
  warning?: boolean;
}) {
  return (
    <div className={classNames(styles.tile, { [styles.warningTile]: warning })}>
      <span className={styles.tileValue}>{value}</span>
      <span className={styles.tileLabel}>{label}</span>
    </div>
  );
}

/**
 * The closing narrative sentence restating the figures in prose (mock §5). The NRA
 * clause and the pricing sentence render only when their values exist (both are
 * `null` until a completed calc); the title-risk sentence always closes it.
 * Pluralization uses the shared `plur`; numbers go through `format0` / `formatAcres`
 * / `formatWholeDollarCurrency` (never raw).
 *
 * @param {object} props - The metrics to restate.
 * @param {ExecutiveSummaryResponse} props.summary - The summary metrics.
 * @return {JSX.Element} The narrative content (to be wrapped in a paragraph).
 */
function SummaryNarrative({ summary }: { summary: ExecutiveSummaryResponse }) {
  const {
    assets,
    operators,
    formations,
    nra,
    brokerTargetValue,
    brokerMaxValue,
    riskedAssetCount,
  } = summary;

  return (
    <>
      This package contains{' '}
      <strong>
        {format0(assets)} {plur('Asset', assets)}
      </strong>{' '}
      across{' '}
      <strong>
        {format0(operators)} {plur('operator', operators)}
      </strong>{' '}
      and{' '}
      <strong>
        {format0(formations)} {plur('formation', formations)}
      </strong>
      {nra !== null && (
        <>
          , representing <strong>{formatAcres(nra)} NRA</strong>
        </>
      )}
      .{' '}
      {brokerTargetValue !== null && brokerMaxValue !== null && (
        <>
          The evaluated Target Value is{' '}
          <strong>{formatWholeDollarCurrency(brokerTargetValue, EMPTY)}</strong>{' '}
          and Max Value is{' '}
          <strong>{formatWholeDollarCurrency(brokerMaxValue, EMPTY)}</strong>
          .{' '}
        </>
      )}
      {riskedAssetCount > 0
        ? `${format0(riskedAssetCount)} ${plur('Asset', riskedAssetCount)} ${
            riskedAssetCount === 1 ? 'has' : 'have'
          } Title Quality or Title Risk Factor applied — evaluated values for these Assets are discounted accordingly.`
        : 'No title risk factors are applied to this package.'}
    </>
  );
}

export interface ExecutiveSummaryViewProps {
  /** The metrics to render, or `undefined` before the first load resolves. */
  summary: ExecutiveSummaryResponse | undefined;
  /** The unified loading umbrella (own `isFetching` OR `useCalcLifecycle.isRunning`). */
  isLoading: boolean;
  /** Any filter active → show the "Filtered view" banner. */
  isFiltered: boolean;
  /**
   * INS-10669 — the working Type Curve config differs from this run's, so the population
   * surfaces are previewing the would-be population (live resolution) rather than the
   * saved run → show the preview banner. Always false on drafts.
   */
  isPreviewing?: boolean;
  /** Fetch error message, or null. */
  error: string | null;
  /**
   * Whether a retry could plausibly succeed. `false` for a PERMANENT failure — the BE
   * envelope's `retryable: false`, e.g. `calculation_data_unavailable` when this calc's
   * slot-allocation snapshot was never written — in which case the banner states the
   * failure WITHOUT a Retry button, rather than inviting the user to hammer an endpoint
   * that has already said no (INS-12433). Defaults to true: an unclassified failure keeps
   * the affordance.
   */
  canRetry?: boolean;
  /** Re-run the summary fetch (the error banner's built-in Retry). */
  onRetry: () => void;
  /**
   * The live interest-modeling contribution to Target / Max, rolled up from the
   * working-config preview (carry net-to-us + each elected UMI outcome). A non-zero
   * contribution shows the roll-up sentence; the zero contribution shows nothing.
   */
  interestModelDelta: InterestModelDelta;
}

/**
 * Pure, prop-driven Executive Summary (mock §5): a grid of stat cards — Target /
 * Max value, Net Royalty Acres, Assets, the four status counts (PDP / DUC /
 * Permit / LTD), Operators,
 * Formations, and (only when > 0) a yellow-warning Assets-with-Title-Risk card —
 * over a closing narrative sentence. Pricing/NRA show the empty indicator until a
 * completed calc backs them (the BE sends `null`).
 *
 * States mirror `PackageOverviewSection` — the tile strip is the PERMANENT fixture
 * (empty-indicator values before the first load); everything else is chrome layered
 * above it:
 *  - **Loading** (the unified umbrella): the strip DIMS in place + the title spinner
 *    (`aria-busy` for a11y) — no skeleton, no swap.
 *  - **Error** — a notification + Retry ABOVE the strip (never replaces it), shown
 *    only when NOT loading (a run/refetch in flight supersedes a stale error).
 *  - The narrative needs real figures, so it renders only once `summary` exists.
 * Numbers go through `FormattedCount` / `formatNumber` /
 * `formatWholeDollarCurrency` (never raw).
 *
 * @param {ExecutiveSummaryViewProps} props - The metrics, fetch state, filtered flag, and retry handler.
 * @return {JSX.Element} The Executive Summary panel.
 */
export function ExecutiveSummaryView({
  summary,
  isLoading,
  isFiltered,
  isPreviewing = false,
  error,
  canRetry = true,
  onRetry,
  interestModelDelta,
}: ExecutiveSummaryViewProps) {
  // 0 both before the first load and when no assets carry risk — either way the
  // conditional warning tile stays hidden.
  const riskedAssetCount = summary?.riskedAssetCount ?? 0;

  return (
    <OutputSection
      title="Executive Summary"
      isLoading={isLoading}
      defaultExpanded
    >
      {/* Clarifies that the count tiles below reflect valued properties only (mock §5). */}
      <p className={styles.caption}>
        Property counts reflect valued properties only.
      </p>

      {/* INS-10669 — the working TC config differs from this run's: every population
          surface is previewing the would-be population live, and run-only values ($,
          verdicts) are unavailable until Calculate. */}
      {isPreviewing && (
        <InlineNotification
          className={styles.banner}
          kind="warning"
          lowContrast
          hideCloseButton
          title="Type Curve configuration changed"
          subtitle="Unchanged properties keep the last run's values; changed type curve slots are shown fresh and unpriced. Calculate to price them."
        />
      )}

      {isFiltered && (
        <InlineNotification
          className={styles.banner}
          kind="info"
          lowContrast
          hideCloseButton
          title="Filtered view"
          subtitle="Showing data based on the filters applied in the left panel."
        />
      )}

      {/* Error is a NOTIFICATION over the fixture, never a replacement — the kept
              (or empty-indicator) tiles stay; Retry lives INSIDE the notification
              (ActionableNotification — InlineNotification can't host actions). Suppressed
              while loading: a run/refetch in flight supersedes a stale error. */}
      {error && !isLoading && canRetry && (
        <ActionableNotification
          className={styles.banner}
          inline
          kind="error"
          // hasFocus off: its auto-focus scrolls the errored section into view (page jump)
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load the summary"
          subtitle={error}
          actionButtonLabel="Retry"
          // Carbon calls this WITH the click event — the connected wrapper passes a
          // zero-arg closure (never RQ's `refetch` bare, whose `options` param would
          // swallow the event), so the direct pass-through is safe.
          onActionButtonClick={onRetry}
        />
      )}

      {/* PERMANENT failure (BE `retryable: false`): same placement and kind, but NO Retry —
          the button could never succeed, and offering it was the whole of the INS-12433
          user experience. The subtitle is the BE envelope's own message, which names the
          missing snapshot and the action that regenerates it, so no copy is hardcoded here
          and a future non-retryable code is never mislabeled. `role="alert"` (Carbon's
          InlineNotification defaults to the quieter "status") — the branch the user can do
          nothing about must not be announced more quietly than the retryable one. */}
      {error && !isLoading && !canRetry && (
        <InlineNotification
          className={styles.banner}
          kind="error"
          role="alert"
          lowContrast
          hideCloseButton
          title="Couldn't load the summary"
          subtitle={error}
        />
      )}

      {/* Permanent fixture — dims in place while loading; aria-busy carries the
              a11y signal. */}
      <div
        className={classNames(styles.content, {
          [styles.dimmed]: isLoading,
        })}
        aria-busy={isLoading}
      >
        <div className={styles.strip}>
          <StatTile
            label="Target Value"
            value={formatWholeDollarCurrency(summary?.brokerTargetValue, EMPTY)}
          />
          <StatTile
            label="Max Value"
            value={formatWholeDollarCurrency(summary?.brokerMaxValue, EMPTY)}
          />
          <StatTile
            label="Net Royalty Acres"
            value={formatAcres(summary?.nra)}
          />
          <StatTile
            label="Net Mineral Acres"
            // The BE doesn't emit `nma` yet — renders `—` until it lands.
            value={formatAcres(summary?.nma)}
          />
          {COUNT_TILES.filter(
            // A Create New Well status shows only when the package has slots in it: most have
            // none, and the four are held unvalued until the flag is on.
            ({ value, wellStatus }) =>
              !wellStatus || (summary ? value(summary) : 0) > 0
          ).map(({ label, value }) => (
            <StatTile
              key={label}
              label={label}
              value={
                <FormattedCount
                  value={summary ? value(summary) : null}
                  fallback={EMPTY}
                />
              }
            />
          ))}
          {riskedAssetCount > 0 && (
            <StatTile
              warning
              label="Assets with Title Risk"
              value={<FormattedCount value={riskedAssetCount} />}
            />
          )}
        </div>
        {hasInterestModelDelta(interestModelDelta) && (
          <p className={styles.interestNote}>
            Interest modeling contributes{' '}
            <strong>{fmtUsd(interestModelDelta.targetDelta)}</strong> to Target
            and <strong>{fmtUsd(interestModelDelta.maxDelta)}</strong> to Max
            Value.
          </p>
        )}
        {summary && (
          <p className={styles.narrative}>
            <SummaryNarrative summary={summary} />
          </p>
        )}
      </div>
    </OutputSection>
  );
}

/**
 * Connected Executive Summary (a business-heavy container): reads the page identity
 * from `useEval3Page()` (package + calc id) plus the dataset filters
 * (`serverFiltersAtom`), spatial scope (`mapScopeAtom`), and active-filter count, then
 * fetches via `useExecutiveSummary` and feeds the pure `ExecutiveSummaryView`. Mounted
 * (gated by the Layout Settings toggle) in `Eval3NewContent`. On a fetch error the View
 * layers a notification + Retry above the kept tiles (mirrors `PackageOverviewSection`).
 *
 * @return {JSX.Element} The connected Executive Summary panel.
 */
export function ExecutiveSummary() {
  const { packageId, calculationId } = useEval3Page();
  const filters = useAtomValue(serverFiltersAtom);
  const mapScope = useAtomValue(mapScopeAtom);
  const activeFilterCount = useAtomValue(activeFilterCountAtom);
  // INS-10669 — the panel's counts are taken over the LTD population, so both the scenario
  // and the TC Manager's choices have to reach it. Neither did: the scenario was never
  // passed at all, so this panel quoted the Legacy default even after a pick.
  const typeCurveScenarioId = useAtomValue(typeCurveScenarioIdAtom);
  const typeCurveOverrides = useAtomValue(typeCurveOverridesAtom);
  const inventoryPackageOverrides = useAtomValue(inventoryPackageOverridesAtom);
  const { data, isFetching, error, refetch } = useExecutiveSummary({
    packageId,
    calculationId,
    typeCurveScenarioId,
    typeCurveOverrides,
    inventoryPackageOverrides,
    filters,
    mapScope,
  });
  // The unified loading umbrella: this panel's OWN summary fetch OR the shared
  // calc-lifecycle wait (detail resolving / BE processing) — one loading language
  // for both causes (mirrors `PackageOverviewSection`).
  const { isRunning } = useCalcLifecycle(calculationId);
  const isLoading = isFetching || isRunning;
  // INS-11129 Phase 2 — TC-diverged: the BE now serves a HYBRID (blob rows with the
  // changed pairs replaced), so most of the page keeps its saved values; the banner
  // explains that only the changed type curve slots are fresh/unpriced. The divergence
  // test here is the same fold comparison the BE's hybrid switch makes, computed
  // client-side (the hook nulls itself for drafts — rules of hooks, called always).
  const populationCalculationId = usePopulationCalculationId(calculationId);
  // The server's verdict from the last master-data response is OR-ed in (PR #7365
  // review — populationMode's consumer): the client comparison flips instantly, the
  // server echo catches any divergence source the client test misses. Keyed by calc id
  // so a previous eval's verdict cannot leak here.
  const serverPopulationMode = useAtomValue(serverPopulationModeAtom);
  const serverSaysHybrid =
    serverPopulationMode?.calculationId === calculationId &&
    serverPopulationMode.mode === 'hybrid';
  const isPreviewing =
    calculationId !== null &&
    (populationCalculationId === null || serverSaysHybrid);
  // The live interest-modeling contribution to Target / Max — written by the
  // always-mounted `InterestModelingSection` from its working-config preview, so the
  // roll-up sentence tracks the working edits (not the last committed run). The zero
  // contribution (nothing modeled) hides the sentence.
  const interestModelDelta = useAtomValue(interestModelDeltaAtom);
  // A non-retryable failure (BE `retryable: false`, e.g. `calculation_data_unavailable`
  // when this calc's slot-allocation snapshot was never written) can never succeed on
  // retry, so the View drops the button rather than offering a dead one (INS-12433).
  // Anything that isn't a typed ExecutiveSummaryError stays retryable.
  const canRetry = !(
    error instanceof ExecutiveSummaryError && !error.retryable
  );

  return (
    <ExecutiveSummaryView
      summary={data}
      isLoading={isLoading}
      isFiltered={activeFilterCount > 0}
      isPreviewing={isPreviewing}
      // The BE envelope's message now (the hook parses it off the ky HTTPError), so this
      // reads "…slot_allocations.parquet was never written… re-run the evaluation…"
      // instead of ky's bare "Unprocessable Content".
      error={error?.message ?? null}
      canRetry={canRetry}
      interestModelDelta={interestModelDelta}
      // Zero-arg closure (not bare `refetch`) — the View forwards `onRetry` to
      // Carbon's onActionButtonClick, which fires with the click event; bare
      // `refetch` would mis-read that event as its RQ options.
      onRetry={() => refetch()}
    />
  );
}
